import type { Cents } from "../domain/money";
import type { ReportIdentity, ReportModel, ReportRow, ReportSection, ReportValue } from "./reportModel";

/**
 * DESIGN A vs DESIGN B (Phase 13, spec §42G).
 *
 * The financial half of comparing two proposed designs. Both sides are
 * evaluated by the SAME estimating engine (`computeForDesign` and the
 * design-scoped proposed lines), so this report is arrangement only — and
 * comparing never touches the Active Design.
 */

export interface DesignScopeRow {
  roomName: string;
  featureName: string;
  amountCents: Cents;
}

export interface DesignComparisonInput {
  identity: ReportIdentity;
  designA: { id: string; name: string; role: string; rows: DesignScopeRow[]; contingencyCents: Cents };
  designB: { id: string; name: string; role: string; rows: DesignScopeRow[]; contingencyCents: Cents };
  generatedAt: string;
}

const money = (cents: Cents): ReportValue => ({ kind: "money", cents });

export function designComparisonReport(input: DesignComparisonInput): ReportModel {
  const keyOf = (row: DesignScopeRow) => `${row.roomName || "Project-wide"}|${row.featureName}`;
  const a = totalsByKey(input.designA.rows, keyOf);
  const b = totalsByKey(input.designB.rows, keyOf);
  const keys = [...new Set([...a.keys(), ...b.keys()])].sort();

  const byRoom = new Map<string, ReportRow[]>();
  for (const key of keys) {
    const [roomName, featureName] = key.split("|");
    const amountA = a.get(key) ?? 0;
    const amountB = b.get(key) ?? 0;
    const rows = byRoom.get(roomName) ?? [];
    rows.push({
      label: featureName,
      note:
        amountA === 0
          ? `Only in ${input.designB.name}`
          : amountB === 0
            ? `Only in ${input.designA.name}`
            : undefined,
      values: [money(amountA), money(amountB), money(amountB - amountA)],
    });
    byRoom.set(roomName, rows);
  }

  const sections: ReportSection[] = [...byRoom.entries()].map(([roomName, rows]) => ({
    title: roomName,
    columnLabel: "Feature",
    columns: [input.designA.name, input.designB.name, "Difference"],
    rows: [
      ...rows,
      {
        label: `${roomName} total`,
        emphasis: "subtotal" as const,
        values: [
          money(rows.reduce((s, r) => s + valueOf(r.values[0]), 0)),
          money(rows.reduce((s, r) => s + valueOf(r.values[1]), 0)),
          money(rows.reduce((s, r) => s + valueOf(r.values[2]), 0)),
        ],
      },
    ],
  }));

  const totalA = sum(input.designA.rows) + input.designA.contingencyCents;
  const totalB = sum(input.designB.rows) + input.designB.contingencyCents;
  sections.push({
    title: "Totals",
    columnLabel: "",
    columns: [input.designA.name, input.designB.name, "Difference"],
    rows: [
      { label: "Scope", values: [money(sum(input.designA.rows)), money(sum(input.designB.rows)), money(sum(input.designB.rows) - sum(input.designA.rows))] },
      { label: "Contingency", values: [money(input.designA.contingencyCents), money(input.designB.contingencyCents), money(input.designB.contingencyCents - input.designA.contingencyCents)] },
      { label: "Total estimate", values: [money(totalA), money(totalB), money(totalB - totalA)], emphasis: "total" },
    ],
  });

  return {
    id: "design-comparison",
    title: "Design comparison",
    subtitle: `${input.designA.name} vs ${input.designB.name}`,
    identity: input.identity,
    optionSummary: `${input.designA.name} (${input.designA.role}) vs ${input.designB.name} (${input.designB.role})`,
    sections,
    disclosures: [
      "Both designs are estimated by the same engine from their own scope. Common project scope appears in both, so only the differences move the total.",
      "Comparing changes nothing: the Active Design, the approved design and every estimate stay exactly as they were.",
    ],
    generatedAt: input.generatedAt,
  };
}

function totalsByKey(rows: DesignScopeRow[], keyOf: (row: DesignScopeRow) => string): Map<string, Cents> {
  const totals = new Map<string, Cents>();
  for (const row of rows) {
    const key = keyOf(row);
    totals.set(key, (totals.get(key) ?? 0) + row.amountCents);
  }
  return totals;
}

function sum(rows: DesignScopeRow[]): Cents {
  return rows.reduce((total, row) => total + row.amountCents, 0);
}

function valueOf(value: ReportValue | undefined): number {
  return value && value.kind === "money" ? value.cents : 0;
}
